import {
  CRITERIA,
  HARD_LIMITS,
  DRYNESS_BUCKETS,
  SUNSHINE_BUCKETS,
} from "./criteria.js";

// ──────────────────────────────────────────────────────────────
// Derived score helpers — voor criteria waar de 1-5 score uit
// data wordt afgeleid (i.p.v. handmatig per town gecureerd).
// ──────────────────────────────────────────────────────────────

// Stuksgewijs-lineaire waardefunctie i.p.v. harde bucketsprongen, zodat
// een paar mm verschil (binnen de meetruis) geen volle scorestap geeft.
function lin(x, x0, x1, y0, y1) {
  if (x <= x0) return y0;
  if (x >= x1) return y1;
  return y0 + (y1 - y0) * (x - x0) / (x1 - x0);
}
export function drynessScoreFromRainfall(mm) {
  if (mm == null || isNaN(mm)) return null;
  return Math.round(lin(mm, 450, 1100, 5, 1) * 100) / 100;
}

export function sunshineScoreFromHours(h) {
  if (h == null || isNaN(h)) return null;
  return Math.round(lin(h, 1900, 3000, 1, 5) * 100) / 100;
}

// Geeft de 1-5 score per criterium. Auto-derived voor dryness/sunshine,
// anders gewoon de town-gecureerde waarde.
function scoreForCriterion(town, c, concelho) {
  if (c.id === "dryness")  return drynessScoreFromRainfall(concelho?.climate?.annual_rainfall_mm);
  if (c.id === "sunshine") return sunshineScoreFromHours(concelho?.climate?.annual_sunshine_h);
  if (c.id === "internet") {
    const v = concelho?.internet_class;
    return v === "fiber" ? 5 : v === "mixed" ? 3 : v ? 2 : null;
  }
  if (c.id === "water") {
    const v = concelho?.water_status;
    return v === "moderate" ? 5 : v === "severe" ? 2 : v === "extreme" ? 1 : v ? 3 : null;
  }
  if (c.id === "tranquility") {
    const off  = town?.scores?.tranquility_offseason ?? town?.scores?.tranquility ?? 0;
    const peak = town?.scores?.tranquility_peak      ?? town?.scores?.tranquility ?? 0;
    return Math.round(off * 0.6 + peak * 0.4);
  }
  return town?.scores?.[c.id] ?? null;
}

export function retreatBonus(town, concelho, settings = {}) {
  if (settings?.retreatMode !== true) return 1.0;
  const alOpen   = concelho?.al_open === true || concelho?.al_open === 1;
  const fitGood  = concelho?.fit_score === "good";
  if (alOpen && fitGood) return 1.25;
  if (alOpen)            return 1.15;
  return 1.0;
}

export function computeScore(town, weights, concelho) {
  let total = 0, maxPossible = 0;
  for (const c of CRITERIA) {
    const w = weights?.[c.id] ?? 0;
    if (w === 0) continue;
    const s = scoreForCriterion(town, c, concelho);
    if (s == null) continue;            // onbekend != slecht: uitsluiten i.p.v. als 0 rekenen
    total += s * w;
    maxPossible += 5 * w;
  }
  return maxPossible > 0 ? (total / maxPossible) * 100 : 0;
}


// Geeft per-criterium breakdown voor TownModal score-display.
// Identieke logica als computeScore — single source of truth.
export function computeBreakdown(town, weights, concelho, settings = {}) {
  const rows = [];
  for (const c of CRITERIA) {
    const w = weights?.[c.id] ?? 0;
    if (w === 0) continue;
    const score = scoreForCriterion(town, c, concelho);
    if (score == null) continue;        // onbekende data niet als 0 tonen/tellen
    rows.push({ id: c.id, label: c.label, hint: c.hint, score, weight: w });
  }
  if (settings?.retreatMode) {
    const bonus = retreatBonus(town, concelho, settings);
    if (bonus > 1) {
      rows.push({
        id: "retreatBonus",
        label: "Retreat-bonus (AL-vergunning)",
        hint: `AL-vergunning beschikbaar — +${Math.round((bonus-1)*100)}% op totaalscore`,
        score: bonus > 1.2 ? 5 : 4,
        weight: 0,
        isBonusRow: true,
        bonusPct: Math.round((bonus - 1) * 100),
      });
    }
  }
  return rows;
}

export function vetoReasons(town, concelho, settings = {}) {
  const reasons = [];
  const hr      = town?.hardRequirements ?? {};
  const climate = concelho?.climate ?? {};

  // Zee-afstand veto
  if (hr.seaDistanceMin != null && hr.seaDistanceMin > HARD_LIMITS.seaDistanceMax)
    reasons.push(`Te ver van zee: ${hr.seaDistanceMin} km (max ${HARD_LIMITS.seaDistanceMax} km)`);

  // Brandrisico veto
  if (concelho?.fire_risk_class != null && concelho.fire_risk_class > HARD_LIMITS.fireRiskMaxClass)
    reasons.push(`Brandrisico klasse ${concelho.fire_risk_class} (max ${HARD_LIMITS.fireRiskMaxClass})`);

  // Hittedagen veto
  if (climate.heat_days_35 != null && climate.heat_days_35 > HARD_LIMITS.heatDays35Max)
    reasons.push(`Te heet: ${climate.heat_days_35} dagen >35°C/jaar (max ${HARD_LIMITS.heatDays35Max})`);

  // Zomerpiek veto
  if (climate.summer_max_p95 != null && climate.summer_max_p95 > HARD_LIMITS.summerMaxP95Max)
    reasons.push(`Zomerhitte P95: ${climate.summer_max_p95}°C (max ${HARD_LIMITS.summerMaxP95Max}°C)`);

  // Neerslag veto
  if (climate.annual_rainfall_mm != null && climate.annual_rainfall_mm > HARD_LIMITS.annualRainfallMaxMm)
    reasons.push(`Te nat: ${climate.annual_rainfall_mm} mm/jaar (max ${HARD_LIMITS.annualRainfallMaxMm} mm)`);

  // Zon-minimum veto
  if (climate.annual_sunshine_h != null && climate.annual_sunshine_h < HARD_LIMITS.annualSunshineMinH)
    reasons.push(`Te weinig zon: ${climate.annual_sunshine_h} uur/jaar (min ${HARD_LIMITS.annualSunshineMinH} uur)`);

  // Waterschaarste veto
  if (settings?.waterVeto && concelho?.water_status === "critical")
    reasons.push(`Kritieke waterschaarste (${concelho.water_note || "droogtegebied"})`);

  return reasons;
}

export function scoreTown(town, weights, concelho, enforceVeto, settings = {}) {
  const rawPct  = computeScore(town, weights, concelho);
  const bonus   = retreatBonus(town, concelho, settings);
  const pct     = Math.min(100, rawPct * bonus);
  const reasons = vetoReasons(town, concelho, settings);
  return {
    pct,
    vetoes: reasons,
    excluded: enforceVeto && reasons.length > 0,
    retreatBonusPct: Math.round((bonus - 1) * 100),
  };
}

export function colorForScore(pct, minPct, maxPct) {
  if (maxPct === minPct) return "#d4a851";
  const norm = (pct - minPct) / (maxPct - minPct);
  if (norm >= 0.66) return "#5a8268";
  if (norm >= 0.33) return "#d4a851";
  return "#b85c4d";
}

export const VETO_COLOR = "#7a2e23";

// ── groupScore: geconsolideerde groeps-score berekening ────────────────
// Single source of truth — gebruik deze in TownModal, ComparePanel, etc.
export function groupScore(groupId, town, concelho) {
  const cl = concelho?.climate ?? {};
  const hr = town?.hardRequirements ?? {};

  switch (groupId) {
    case "climate": {
      const mm  = cl.annual_rainfall_mm;
      const sun = cl.annual_sunshine_h;
      const dry      = mm  == null ? null : drynessScoreFromRainfall(mm);
      const sunScore = sun == null ? null : sunshineScoreFromHours(sun);
      if (dry == null && sunScore == null) return null;
      const vals = [dry, sunScore].filter(v => v != null);
      return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
    }
    case "heat": {
      const hd  = cl.heat_days_35;
      const p95 = cl.summer_max_p95;
      const hdScore  = hd  == null ? null : hd  <= 5 ? 5 : hd  <= 15 ? 4 : hd  <= 25 ? 3 : hd  <= 40 ? 2 : 1;
      const p95Score = p95 == null ? null : p95 <= 28 ? 5 : p95 <= 32 ? 4 : p95 <= 36 ? 3 : p95 <= 39 ? 2 : 1;
      const vals = [hdScore, p95Score].filter(v => v != null);
      if (!vals.length) return null;
      return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
    }
    case "sea": {
      const km = hr.seaDistanceMin;
      if (km == null) return null;
      return km <= 10 ? 5 : km <= 20 ? 4 : km <= 30 ? 3 : km <= 50 ? 2 : 1;
    }
    case "fire": {
      const fc = concelho?.fire_risk_class;
      if (fc == null) return null;
      return fc === 1 ? 5 : fc === 2 ? 4 : fc === 3 ? 3 : fc === 4 ? 2 : 1;
    }
    default:
      return null;
  }
}
